import type { Aggregates, AreaId, ChoiceOption, RatingTally, Theme } from '../types/pulse'
import { areas, followUps } from '../data/pulse'

export interface DistributionRow {
  id: string
  label: string
  pct: number
  isMine: boolean
}

function toPct(value: number, total: number): number {
  if (total <= 0) return 0
  return Math.round((value / total) * 100)
}

/** Share of delegates ranking each area #1, highest first. */
export function rankFirstRows(agg: Aggregates, mine: AreaId | null): DistributionRow[] {
  const total = areas.reduce((sum, a) => sum + agg.rankFirst[a.id], 0)
  return areas
    .map((a) => ({
      id: a.id,
      label: a.label,
      pct: toPct(agg.rankFirst[a.id], total),
      isMine: a.id === mine,
    }))
    .sort((a, b) => b.pct - a.pct)
}

/** Distribution across the structured options for one area, highest first. */
export function followUpRows(
  agg: Aggregates,
  area: AreaId,
  mine: string | null,
): DistributionRow[] {
  const counts = agg.followUp[area]
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0)
  return followUps[area].options
    .map((opt: ChoiceOption) => ({
      id: opt.id,
      label: opt.label,
      pct: toPct(counts[opt.id] ?? 0, total),
      isMine: opt.id === mine,
    }))
    .sort((a, b) => b.pct - a.pct)
}

/**
 * A neutral read on where the delegate sits relative to the room. Never frames
 * an answer as right or popular-therefore-correct.
 */
export function alignmentNote(rows: DistributionRow[]): string {
  const mine = rows.find((r) => r.isMine)
  if (!mine) return ''
  const leader = rows.reduce((top, row) => (row.pct > top.pct ? row : top), rows[0])

  if (mine.id === leader.id) {
    return `Most of your peers see it the same way — ${mine.pct}% of NOK delegates so far chose this too.`
  }
  if (mine.pct >= 25) {
    return `A sizeable group of your peers agrees — ${mine.pct}% chose this, while “${leader.label}” leads so far on ${leader.pct}%.`
  }
  return `Your view is less common so far — ${mine.pct}% chose this, while “${leader.label}” leads on ${leader.pct}%.`
}

/** The same neutral read for the 1–5 scales. */
export function ratingAlignmentNote(value: number, average: number, highPct: number): string {
  const gap = value - average
  if (Math.abs(gap) <= 0.5) {
    return `Most of your peers land where you do — the NOK average is ${average.toFixed(1)}.`
  }
  if (gap > 0.5) {
    return `You see more potential here than the room so far — ${highPct}% of delegates rated it 4 or 5.`
  }
  return `You are more cautious than the room so far — ${highPct}% of delegates rated it 4 or 5.`
}

export function ratingStats(tally: RatingTally): { average: string; highPct: number } {
  const average = tally.n > 0 ? (tally.sum / tally.n).toFixed(1) : '0.0'
  return { average, highPct: toPct(tally.high, tally.n) }
}

export function sortedThemes(agg: Aggregates): Theme[] {
  return [...agg.themes].sort((a, b) => b.count - a.count)
}

/**
 * Themes closest to what the delegate wrote, topped up with the busiest
 * themes in the room so the cluster always reads as shared ground.
 */
export function relatedThemes(agg: Aggregates, text: string, limit: number): Theme[] {
  const value = text.toLowerCase()
  const matched = agg.themes.filter((t) => t.match.some((k) => value.includes(k)))
  const rest = sortedThemes(agg).filter((t) => !matched.includes(t))
  return [...matched.sort((a, b) => b.count - a.count), ...rest].slice(0, limit)
}

/** Lightweight keyword attribution so themes reflect what was actually submitted. */
export function matchTheme(themes: Theme[], text: string): string | null {
  const value = text.toLowerCase()
  if (!value.trim()) return null
  for (const theme of themes) {
    if (theme.match.some((keyword) => value.includes(keyword))) return theme.id
  }
  return null
}

export function areaLabel(id: AreaId): string {
  return areas.find((a) => a.id === id)?.label ?? ''
}
